"use client";

import React from 'react';
import Link from 'next/link';
import Image from "next/image";
import siteConfig from '@/config/siteConfig';

const Footer = () => {
  return (
    <footer className="bg-[#0F2942] text-white">
      <div className="container mx-auto px-4 py-12">
        {/* Footer content grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Logo and description */}
          <div className="lg:col-span-2">
            <Link href="/" className="inline-block mb-4">
              <div className="relative h-10 w-32">
                <Image
                  src="/images/logo.png"
                  alt="Perxify logo"
                  fill
                  className="object-contain max-w-full h-auto block min-w-[120px] [filter:brightness(0)_invert(1)]"
                  priority={true}
                  sizes="128px"
                />
              </div>
            </Link>
            <p className="text-gray-300 mb-4">
              AI-native Commerce Cloud platform enabling brands to build innovative commerce experiences with analytics-driven insights through shared infrastructure.
            </p>
            <div className="flex space-x-4">
              <a href={siteConfig.organization.social.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="LinkedIn">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a href={siteConfig.organization.social.x} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="X (Twitter)">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.205 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.97 6.817H1.64l7.73-8.835L1.215 2.25H8.04l4.713 6.231 5.452-6.231Zm-1.161 17.52h1.833L7.045 4.126H5.078L16.044 19.77Z" />
                </svg>
              </a>
              <a href={siteConfig.organization.social.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="Facebook">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </div>
          </div>
          
          {/* Platform links */}
          <div className="max-[1023px]:hidden">
            <h3 className="text-lg font-semibold mb-4">Platform</h3>
            <ul className="space-y-2">
              <li><Link href="/platform/overview" className="text-gray-300 hover:text-white transition-colors">Overview</Link></li>
              <li><Link href="/platform/e-commerce-module" className="text-gray-300 hover:text-white transition-colors">E-commerce Module</Link></li>
              <li><Link href="/platform/marketplace-module" className="text-gray-300 hover:text-white transition-colors">Marketplace Module</Link></li>
              <li><Link href="/platform/analytics-insights" className="text-gray-300 hover:text-white transition-colors">Analytics & Insights</Link></li>
              <li><Link href="/platform/loyalty-rewards" className="text-gray-300 hover:text-white transition-colors">Loyalty & Rewards</Link></li>
            </ul>
          </div>

          {/* Solutions links */}
          <div className="max-[1023px]:hidden">
            <h3 className="text-lg font-semibold mb-4">Solutions</h3>
            <ul className="space-y-2">
              <li><Link href="/solutions/consumer-brands" className="text-gray-300 hover:text-white transition-colors">Consumer Brands</Link></li>
              <li><Link href="/solutions/sport-entertainment-brands" className="text-gray-300 hover:text-white transition-colors">Sport & Entertainment</Link></li>
              <li><Link href="/solutions/food-beverage" className="text-gray-300 hover:text-white transition-colors">Food & Beverage</Link></li>
            </ul>
          </div>
          
          {/* Company links */}
          <div className="max-[1023px]:hidden">
            <h3 className="text-lg font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              <li><Link href="/about" className="text-gray-300 hover:text-white transition-colors">About Us</Link></li>
              <li><Link href="/case-studies/booksshark" className="text-gray-300 hover:text-white transition-colors">BooksShark Showcase</Link></li>
              <li><Link href="/blog" className="text-gray-300 hover:text-white transition-colors">Blog</Link></li>
              <li><Link href="/pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</Link></li>
              <li><Link href="/faq" className="text-gray-300 hover:text-white transition-colors">FAQ</Link></li>
            </ul>
          </div>

          {/* New links column - shown below 1024px */}
          <div className="lg:hidden">
            <h3 className="text-lg font-semibold mb-4">Links</h3>
            <ul className="space-y-2">
              <li><Link href="/platform/overview" className="text-gray-300 hover:text-white transition-colors">Platform Overview</Link></li>
              <li><Link href="/case-studies/booksshark" className="text-gray-300 hover:text-white transition-colors">Case Study</Link></li>
              <li><Link href="/about" className="text-gray-300 hover:text-white transition-colors">About Us</Link></li>
              <li><Link href="/pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</Link></li>
              <li><Link href="/blog" className="text-gray-300 hover:text-white transition-colors">Blog</Link></li>
              <li><Link href="/faq" className="text-gray-300 hover:text-white transition-colors">FAQ</Link></li>
            </ul>
          </div>
        </div>
        
        {/* Bottom footer */}
        <div className="border-t border-gray-700 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm mb-4 md:mb-0">
            &copy; {new Date().getFullYear()} Perxify. All rights reserved.
          </p>
          <div className="flex space-x-6">
            <Link href="/privacy-policy" className="text-gray-400 hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link href="/terms-of-service" className="text-gray-400 hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;